/**
 * Auth Storage Utility
 * Handles parsing base64 encoded auth data and localStorage operations
 */

// Key mappings from payload fields to localStorage keys
const KEY_MAPPINGS: Record<string, string> = {
    firebaseAuthToken: 'auth-token',
    userId: 'user-id',
    userType: 'user-type',
    organisationId: 'organisation-id',
    accessToken: 'access-token',
};

const KEY_MAPPINGS_REVERSE: Record<string, string> = {
    'auth-token': 'firebaseAuthToken',
    'user-id': 'userId',
    'user-type': 'userType',
    'organisation-id': 'organisationId',
    'access-token': 'accessToken',
};

export interface AuthPayload {
    firebaseAuthToken?: string;
    userId?: string;
    userType?: string;
    organisationId?: string;
    accessToken?: string;
}

export interface AuthPayloadExternal {
    'auth-token'?: string;
    'user-id'?: string;
    'user-type'?: string;
    'organisation-id'?: string;
    'access-token'?: string;
}

/**
 * Parse base64 encoded auth data
 */
export const parseBase64AuthData = (base64String: string): AuthPayloadExternal | null => {
    try {
        const decoded = atob(base64String);
        const parsed = JSON.parse(decoded);
        return parsed as AuthPayloadExternal;
    } catch (error) {
        console.error('Failed to parse base64 auth data:', error);
        return null;
    }
};

/**
 * Store auth data to localStorage (keys already in localStorage format)
 */
export const storeAuthData = (authData: AuthPayloadExternal): void => {
    Object.entries(authData).forEach(([key, value]) => {
        const keyToStore = KEY_MAPPINGS_REVERSE[key];
        if (keyToStore && value !== undefined && value !== null) {
            localStorage.setItem(keyToStore, String(value));
        }
    });
};

/**
 * Get a specific auth value from localStorage
 */
export const getAuthValue = (key: keyof AuthPayload): string => {
    return localStorage.getItem(key) || '';
};

/**
 * Get all auth data from localStorage
 */
export const getAuthData = (): AuthPayload => ({
    firebaseAuthToken: localStorage.getItem('firebaseAuthToken') || '',
    userId: localStorage.getItem('userId') || '',
    userType: localStorage.getItem('userType') || '',
    organisationId: localStorage.getItem('organisationId') || '',
    accessToken: localStorage.getItem('accessToken') || '',
});

/**
 * Check if user is authenticated (has accessToken in localStorage)
 */
export const isAuthenticated = (): boolean => {
    const accessToken = localStorage.getItem('firebaseAuthToken');
    return !!accessToken && accessToken.length > 0;
};

/**
 * Clear all auth data from localStorage
 */
export const clearAuthData = (): void => {
    Object.values(KEY_MAPPINGS).forEach((storageKey) => {
        localStorage.removeItem(storageKey);
    });
};
